import { CircleCheck, CircleX, GitMerge, Info, type LucideIcon, Radio, TriangleAlert } from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import type { TickerItem } from "../../store";
import { EmptyState } from "../page";
import { StatusDot } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";

const LEVEL: Record<TickerItem["level"], { icon: LucideIcon; className: string }> = {
  info: { icon: Info, className: "text-sky-600 dark:text-sky-400" },
  success: { icon: CircleCheck, className: "text-emerald-600 dark:text-emerald-400" },
  warning: { icon: TriangleAlert, className: "text-amber-600 dark:text-amber-400" },
  error: { icon: CircleX, className: "text-red-600 dark:text-red-400" },
};

export function ActivityFeed({ items, live }: { items: TickerItem[]; live: boolean }) {
  return (
    <Card className="gap-0 pb-0">
      <CardHeader className="border-b">
        <CardTitle>Live activity</CardTitle>
        <CardDescription>Station updates as they happen</CardDescription>
        <CardAction>
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <StatusDot tone={live ? "emerald" : "sky"} pulse={live} />
            {live ? "Live" : "Polling"}
          </span>
        </CardAction>
      </CardHeader>
      <CardContent className="px-0">
        {items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={Radio}
              title="Quiet on the floor"
              description="Activity shows up here as it happens."
            />
          </div>
        ) : (
          <ScrollArea className="h-[26rem]">
            <ol className="divide-y">
              {items.map((item) => {
                const { icon: Icon, className } =
                  item.kind === "merge" && item.level === "info"
                    ? { icon: GitMerge, className: "text-violet-600 dark:text-violet-400" }
                    : LEVEL[item.level];
                const body = (
                  <>
                    <Icon className={cn("mt-0.5 size-4 shrink-0", className)} />
                    <span className="min-w-0 flex-1 break-words">{item.text}</span>
                    <TimeAgo at={item.at} className="shrink-0 text-xs text-muted-foreground tabular-nums" />
                  </>
                );
                return (
                  <li key={item.id}>
                    {item.taskId ? (
                      <Link
                        to={`/tasks/${item.taskId}`}
                        className="flex gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted/60"
                      >
                        {body}
                      </Link>
                    ) : (
                      <div className="flex gap-3 px-4 py-2.5 text-sm">{body}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
